import React, { useEffect, useState } from "react";
import { Plugs, MetaLogo } from "@phosphor-icons/react";

import { acquisition as acquisitionApi } from "@/api";
import ActaStandardPage from "@/components/layout/ActaStandardPage";

export default function AcquisitionConnections() {
  const [items, setItems] = useState([]);
  const [metaConfig, setMetaConfig] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      const [rows, config] = await Promise.all([
        acquisitionApi.listConnections(),
        acquisitionApi.metaConfiguration(),
      ]);
      setItems(Array.isArray(rows) ? rows : []);
      setMetaConfig(config || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de charger les connexions.");
    }
  }

  useEffect(() => { load(); }, []);

  async function connectMeta() {
    setConnecting(true);
    setError("");
    try {
      const result = await acquisitionApi.metaOauthUrl();
      if (!result?.url) throw new Error("URL OAuth Meta indisponible.");
      window.location.assign(result.url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Connexion Meta impossible.");
      setConnecting(false);
    }
  }

  async function testMeta() {
    setConnecting(true);
    setError("");
    try {
      const result = await acquisitionApi.testMeta();
      if (!result?.ok) {
        setError("La connexion Meta présente une erreur.");
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Test Meta impossible.");
    } finally {
      setConnecting(false);
    }
  }

  async function syncMeta() {
    setConnecting(true);
    setError("");
    try {
      const result = await acquisitionApi.syncMeta();
      if (result?.errors?.length) {
        setError(`Synchronisation terminée avec ${result.errors.length} erreur(s).`);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Synchronisation Meta impossible.");
    } finally {
      setConnecting(false);
    }
  }

  async function disconnectMeta() {
    setConnecting(true);
    setError("");
    try {
      await acquisitionApi.disconnectMeta();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Déconnexion Meta impossible.");
    } finally {
      setConnecting(false);
    }
  }

  const meta = items.find((item) => item.provider === "meta");

  return (
    <ActaStandardPage testId="acquisition-connections">
      {error ? <div className="mt-4 rounded-2xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-4 text-sm text-[var(--do-danger)]">{error}</div> : null}

      <section className="lirah-card mt-6 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
              <MetaLogo size={26} weight="bold" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">Meta Facebook / Instagram</h2>
              <p className="mt-1 text-sm text-[var(--do-muted)]">
                {meta ? `État : ${meta.status || "inconnu"}` : "Aucune connexion configurée."}
              </p>
              {meta?.lastSyncAt ? <p className="mt-1 text-xs text-[var(--do-muted)]">Dernière synchronisation : {new Date(meta.lastSyncAt).toLocaleString("fr-FR")}</p> : null}
              {meta?.lastTestAt ? <p className="mt-1 text-xs text-[var(--do-muted)]">Dernier test : {new Date(meta.lastTestAt).toLocaleString("fr-FR")}</p> : null}
              {meta?.pages?.length ? (
                <div className="mt-3 text-xs text-[var(--do-muted)]">
                  Pages autorisées : {meta.pages.map((page) => page.name || page.id).join(", ")}
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {meta?.status === "connected" ? (
              <button type="button" className="btn-secondary px-4 py-3" disabled={connecting} onClick={testMeta}>
                Tester
              </button>
            ) : null}
            {meta?.status === "connected" ? (
              <button type="button" className="btn-secondary px-4 py-3" disabled={connecting} onClick={syncMeta}>
                Synchroniser
              </button>
            ) : null}
            {meta?.status === "connected" ? (
              <button type="button" className="btn-secondary px-4 py-3" disabled={connecting} onClick={disconnectMeta}>
                Déconnecter
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary px-4 py-3"
              disabled={connecting || metaConfig?.configured === false}
              onClick={connectMeta}
              title={metaConfig?.configured === false ? "Configuration serveur Meta incomplète" : ""}
            >
              <Plugs size={18} /> {connecting ? "Connexion…" : meta ? "Reconnecter" : "Connecter Meta"}
            </button>
          </div>
        </div>
        <div className="mt-5 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg)] p-4 text-sm text-[var(--do-muted)]">
          Le connecteur n’expose jamais les tokens dans l’interface. Les credentials sont chiffrés côté backend et les permissions OAuth sont fournies par la configuration serveur.
          {metaConfig?.configured === false ? (
            <div className="mt-2 font-bold text-[var(--do-danger)]">Configuration Meta serveur incomplète.</div>
          ) : null}
        </div>
      </section>
    </ActaStandardPage>
  );
}
